import { useEffect, useRef } from "react";

// DOM event avoids the circular @library ↔ @editor-elements import.
export const TABLE_FORMULA_COMMIT_EVENT = "table-formula-edit-commit";
export interface ITableFormulaCommitDetail {
  entityId: string;
  cellId: string;
  buffer: string;
}

// Blur does NOT commit (it races the popover's focus-restore); Enter commits, Esc cancels.
export const FormulaEditInput = ({
  entityId,
  cellId,
  buffer,
  onUpdate,
  onStop,
}: {
  entityId: string;
  cellId: string;
  buffer: string;
  onUpdate: (buffer: string) => void;
  onStop: () => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Defer one task so our focus wins the race against the popover's restore.
    const id = setTimeout(() => {
      el.focus();
      const openIdx = buffer.indexOf("(");
      const closeIdx = buffer.lastIndexOf(")");
      if (openIdx >= 0 && closeIdx > openIdx) {
        const insidePos = closeIdx;
        el.setSelectionRange(insidePos, insidePos);
      } else {
        el.setSelectionRange(buffer.length, buffer.length);
      }
    }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = () => {
    // Blur before unmount so focus isn't orphaned on the disconnected node.
    ref.current?.blur();
    const detail: ITableFormulaCommitDetail = {
      entityId,
      cellId,
      buffer: buffer.trim(),
    };
    document.dispatchEvent(
      new CustomEvent(TABLE_FORMULA_COMMIT_EVENT, { detail }),
    );
    onStop();
  };

  const cancel = () => {
    ref.current?.blur();
    onStop();
  };

  // Esc/Enter while ref-picking (input not focused); no deps so it closes over latest buffer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.activeElement === ref.current) return;
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      } else if (e.key === "Enter") {
        e.preventDefault();
        commit();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  return (
    <input
      ref={ref}
      type="text"
      value={buffer}
      onChange={(e) => onUpdate(e.target.value)}
      onKeyDown={(e) => {
        // Stop the native event bubbling to the document keydown listener below:
        // commit()/cancel() blur synchronously, which would defeat that
        // listener's "input focused?" guard and fire commit/cancel twice.
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          commit();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          cancel();
        }
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      className="tw-w-full tw-min-w-[2ch] tw-bg-transparent tw-border-0 tw-outline-none"
      style={{
        font: "inherit",
        color: "inherit",
        padding: 0,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}
      aria-label="Formula"
    />
  );
};
